import type { FieldResult } from '../shared/desktop-api';

/**
 * Where a fast-reader value's score comes from, in plain words. The engine scores a
 * fast-reader (GLiNER) value by how often that reader was right on this field on this
 * job (FastReaderTrackRecord), so the Field values threshold means what it says: a value
 * scored 80 is right about 80% of the time. Null for every other route, and once a
 * person has confirmed the value.
 */
export function confidenceBasisText(
  field:
    Pick<FieldResult, 'confidence' | 'verified' | 'confidenceBasis' | 'peerEvidence'> | undefined,
): string | null {
  const peers = field?.peerEvidence;
  if (field && peers && !field.verified) {
    // DocumentStore.FieldPeerEvidence: measured on the sample sets, never on this job.
    if (!peers.printedHere) return 'Not found on the page';
    if (peers.leadingWordsOf)
      return `Looks cut short. Similar documents say "${peers.leadingWordsOf}"`;
    if (peers.shapeUnlike) return `Doesn't look like other answers, such as "${peers.shapeUnlike}"`;
    // Under a different printed label than the one similar documents answer this field from.
    if (peers.otherBox)
      return peers.peerLabel
        ? `Similar documents print this under "${peers.peerLabel}"`
        : 'Similar documents print this under another label';
    const formText =
      peers.peers >= 3 &&
      peers.printedOn >= 0.8 * peers.peers &&
      peers.sameAnswer < 0.5 * peers.peers;
    // The reader's own record on this field, from the documents the operator confirmed.
    if (!formText && (peers.recordAnswers ?? 0) > 0)
      return `Right on ${peers.recordRight} of ${peers.recordAnswers} documents you confirmed`;
    // Only form text is "printed on every similar document"; a box completed or replaced says nothing here.
    return formText ? 'Printed on every similar document, so probably not this field' : null;
  }
  const basis = field?.confidenceBasis;
  if (!field || !basis || field.verified) return null;
  if (basis.answers === 0) return 'New field, checked until it proves itself';
  if (basis.looksConfirmed === false) return "Doesn't look like the values you confirmed";
  if (basis.shapeAnswers < basis.answers)
    return `Right ${basis.shapeRight} of ${basis.shapeAnswers} times for values like this`;
  return `Right ${basis.right} of ${basis.answers} times on this job`;
}
